import { requestApi } from 'api/api'
import {
  Contact,
  ContactCase,
  ContactCaseCount,
  ContactInstrument,
  ContactInstrumentProps,
  ContactInstruments,
  ContactPayload,
  ContactPaymentInstrumentsResponse,
  Contacts,
  ContactsRequest,
  CreateContactBankAccountInstrumentProps,
  CreateContactBankAccountResponse,
  PostContactCase,
  PutContactProps,
} from 'types/contacts'
import { TwoFAAuthRequest } from 'types/generics'
import { Paging } from 'types/types'
import { API_PAGINATION_LIMIT, API_PAGINATION_OFFSET } from 'utils/pagination'

const getSearchQuery = (searchTerm: string, enableTravelRuleSelfHosted: boolean) => {
  if (enableTravelRuleSelfHosted) {
    return searchTerm
      ? `${searchTerm} verificationStatuses:VERIFIED`
      : 'verificationStatuses:VERIFIED'
  }

  return searchTerm || ''
}
export const getContacts = ({
  size = API_PAGINATION_LIMIT,
  page = 0,
  searchQuery = '',
  contactIds = [],
}: ContactsRequest): Promise<Contacts> => {
  return requestApi({
    baseUrl: process.env.API_URL,
    url: '/platform/v1/contacts',
    method: 'GET',
    params: {
      ...(size && { size }),
      ...(page && { page }),
      ...(searchQuery && { q: searchQuery }),
      ...(contactIds && contactIds?.length > 0 && { ids: contactIds.join(',') }),
    },
  })
}

export const postContact = (data: ContactPayload): Promise<Contact> => {
  return requestApi({
    baseUrl: process.env.API_URL,
    url: '/platform/v1/contacts',
    method: 'POST',
    data,
  })
}

export const putContact = ({ contactId, contact }: PutContactProps): Promise<Contact> => {
  return requestApi({
    baseUrl: process.env.API_URL,
    url: `/platform/v1/contacts/${contactId}`,
    method: 'PUT',
    data: contact,
  })
}

export const getContact = (contactId: string): Promise<Contact> => {
  return requestApi({
    baseUrl: process.env.API_URL,
    url: `/platform/v1/contacts/${contactId}`,
    method: 'GET',
  })
}

export const getContactCaseCount = (): Promise<ContactCaseCount> => {
  return requestApi({
    url: '/payments/v1/travel-rule-rfis/count',
    method: 'GET',
  })
}

export const getContactCases = async ({
  paging,
}: {
  paging: Paging
}): Promise<{ results: ContactCase[]; paging: Paging }> => {
  const { offset, max } = paging

  const { data, headers } = await requestApi({
    url: '/payments/v1/travel-rule-rfis',
    fullResponse: true,
    method: 'GET',
    params: {
      ...(max && { max }),
      offset,
    },
  })

  return {
    results: data,
    paging: {
      total: Number(headers['x-api-list-total']) || 0,
      offset: Number(headers['x-api-list-start']) || offset,
      max,
    },
  }
}

export const postContactCase = (data: PostContactCase): Promise<ContactCase> => {
  return requestApi({
    url: `/payments/v1/travel-rule-rfis/${data?.externalId}/assign-contact`,
    method: 'POST',
    data: { contactId: data?.contactId },
  })
}

export const getContactInstrument = ({
  size = API_PAGINATION_LIMIT,
  page = API_PAGINATION_OFFSET,
  searchQuery = '',
  contactId,
}: ContactsRequest): Promise<ContactInstruments> => {
  return requestApi({
    baseUrl: process.env.API_URL,
    url: `/platform/v1/contacts/${contactId}/instruments`,
    method: 'GET',
    params: {
      ...(size && { size }),
      ...(page && { page }),
      ...(searchQuery && { q: searchQuery }),
    },
  })
}

export const postContactInstrument = ({
  contactId,
  instrument,
}: ContactInstrumentProps): Promise<ContactInstrument> => {
  return requestApi({
    baseUrl: process.env.API_URL,
    url: `/platform/v1/contacts/${contactId}/instruments`,
    method: 'POST',
    data: instrument,
  })
}

export const putContactInstrument = ({
  instrumentId,
  contactId,
  instrument,
}: ContactInstrumentProps): Promise<ContactInstrument> => {
  return requestApi({
    baseUrl: process.env.API_URL,
    url: `/platform/v1/contacts/${contactId}/instruments/${instrumentId}`,
    method: 'PUT',
    data: instrument,
  })
}

export const deleteContactInstrument = ({
  instrumentId,
  contactId,
}: ContactInstrumentProps): Promise<void> => {
  return requestApi({
    baseUrl: process.env.API_URL,
    url: `/platform/v1/contacts/${contactId}/instruments/${instrumentId}`,
    method: 'DELETE',
  })
}

export const deleteContact = ({ contactId }: ContactInstrumentProps): Promise<void> => {
  return requestApi({
    baseUrl: process.env.API_URL,
    url: `/platform/v1/contacts/${contactId}`,
    method: 'DELETE',
  })
}

export const getContactInstruments = (
  searchTerm: string,
  size = API_PAGINATION_LIMIT,
  page = API_PAGINATION_OFFSET,
  sort = '',
  enableTravelRuleSelfHosted = false
): Promise<ContactPaymentInstrumentsResponse> => {
  // Build the 'q' parameter based on feature flag and searchTerm
  const q = getSearchQuery(searchTerm, enableTravelRuleSelfHosted)

  return requestApi({
    baseUrl: process.env.API_URL,
    url: '/platform/v1/payment-instruments',
    method: 'GET',
    params: {
      size,
      page,
      ...(sort && { sort }),
      ...(q && { q }),
    },
  })
}

export const postCreateContactBankAccount = ({
  contactId,
  request,
}: CreateContactBankAccountInstrumentProps): Promise<CreateContactBankAccountResponse> => {
  return requestApi({
    baseUrl: process.env.API_URL,
    url: `/platform/v1/contacts/${contactId}/instruments`,
    method: 'POST',
    data: request,
  })
}

export const postCreateContactBankAccountTwoFactorAuth = ({
  callbackUrl,
  challenge,
  tokenResponse,
}: TwoFAAuthRequest): Promise<CreateContactBankAccountResponse> => {
  return requestApi({
    baseUrl: callbackUrl,
    url: '',
    method: 'POST',
    data: {
      challenge,
      tokenResponse,
    },
  })
}
